import { useQuery } from '@tanstack/react-query';
import ApiClinicians from '../api/api.clinicians';

type UseCliniciansForSlotParams = {
  datetime?: string;
  patientGuid?: string;
  clinSubtype?: string;
  clinSpeccat?: string;
  clinSpecarea?: string;
  apptTypeCode?: string;
  caseId?: number | null;
  rank?: string | number;
  page?: number;
  rows?: number;
  enabled?: boolean;
};

const normalizeClinician = (item: any) => {
  if (!item) return item;
  const source = item?.clinician && typeof item.clinician === 'object' ? { ...item, ...item.clinician } : item;

  const guid =
    source.clin_user_guid || source.user_guid || source.clinician_user_guid || source.guid || source.id || '';
  const firstName = source.clin_first_name || source.first_name || source.firstName || '';
  const lastName = source.clin_last_name || source.last_name || source.lastName || '';
  const title = source.clin_title || source.title || source.salutation || source.clin_salutation || '';
  const jobTitle =
    source.clin_job_title || source.job_title || source.specialty || source.spec_desc || source.clin_subtype_desc || '';
  const photoUrl = source.clin_photo_url || source.photo_url || source.avatar_url || source.profile_image_url || '';
  const gender = source.clin_gender || source.gender || source.sex || '';

  const fullName =
    source.name || source.fullName || source.full_name || [title, firstName, lastName].filter(Boolean).join(' ');

  const normalized = {
    ...source,
    clin_user_guid: guid,
    user_guid: guid,
    guid,
    id: guid,
    clin_first_name: firstName,
    first_name: firstName,
    clin_last_name: lastName,
    last_name: lastName,
    clin_title: title,
    title,
    salutation: title,
    clin_salutation: title,
    clin_job_title: jobTitle,
    job_title: jobTitle,
    specialty: jobTitle,
    clin_photo_url: photoUrl,
    photo_url: photoUrl,
    clin_gender: gender,
    gender,
    name: fullName,
    fullName,
    full_name: fullName
  };

  return {
    ...normalized,
    clinician: normalized
  };
};

export const useCliniciansForSlot = ({
  datetime,
  patientGuid,
  clinSubtype,
  clinSpeccat,
  clinSpecarea,
  apptTypeCode,
  caseId,
  rank = '1',
  page = 1,
  rows,
  enabled = true
}: UseCliniciansForSlotParams) => {
  return useQuery({
    queryKey: [
      'cliniciansForSlot',
      datetime,
      patientGuid,
      clinSubtype,
      clinSpeccat,
      clinSpecarea,
      apptTypeCode,
      caseId,
      rank,
      page,
      rows
    ],
    queryFn: async () => {
      const data = await ApiClinicians.getAll({
        datetime,
        patient_guid: patientGuid,
        subtype: clinSubtype,
        spec_cat: clinSpeccat,
        specarea: clinSpecarea,
        appt_type_code: apptTypeCode,
        case_id: caseId,
        rank: String(rank),
        page,
        rows,
        tag_filter: true
      });

      const rawList = Array.isArray(data) ? data : (data as any)?.clinicians || [];
      return rawList.map(normalizeClinician);
    },
    enabled: enabled && !!datetime && !!patientGuid
  });
};
